/**
 * 定时器管理 Composable
 * 统一管理所有定时器的创建和清理
 */

import config from '@/config';
import logger from '@/utils/logger';

export default function useTimers() {
  return {
    data() {
      return {
        progressTimer: null,
        dataRefreshTimer: null,
        rotationTimer: null,
        timeUpdateTimer: null,
        welcomeMessageTimer: null,
        realtimeTimer: null,
        sleepStateTimer: null,
        chartUpdateTimer: null,
        bubbleTimer: null,
        brainwaveDisplayTimer: null,
        videoTimer: null,
        videoPreloadTimer: null,
        hourlyRecoveryTimer: null,
        hourlyRecoveryStarter: null,
        midnightResetTimer: null,
        currentTimeValue: '', // 存储当前时间值
        fetchAppointmentDataRetryTimer: null, // fetchAppointmentData 等轮播结束再刷新的第一层延迟
        fetchAppointmentDataRetryTimer2: null, // 第二层 500ms 延迟
        fetchAppointmentDataDelayTimer: null, // 轮播刚结束 1.5 秒内延迟刷新
      };
    },

    methods: {
      /**
       * 启动时间更新定时器
       */
      startTimeUpdateTimer() {
        if (this.timeUpdateTimer) {
          clearInterval(this.timeUpdateTimer);
        }
        this.timeUpdateTimer = setInterval(() => {
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          this.currentTimeValue = `${hours}:${minutes}:${seconds}`;
        }, 1000);
      },

      /**
       * 初始化所有定时器
       */
      initAllTimers() {
        const performanceConfig = config.performance || {};
        const chartUpdateMs = Number(performanceConfig.chartUpdateMs) || 2000;
        const brainwaveDisplayMs = Number(performanceConfig.brainwaveDisplayMs) || 1000;
        const bubbleUpdateMs = Number(performanceConfig.bubbleUpdateMs);

        // 清理所有定时器
        if (this.progressTimer) {
          clearInterval(this.progressTimer);
        }
        if (this.dataRefreshTimer) {
          clearInterval(this.dataRefreshTimer);
        }
        if (this.chartUpdateTimer) {
          clearInterval(this.chartUpdateTimer);
        }
        if (this.bubbleTimer) {
          clearInterval(this.bubbleTimer);
        }
        if (this.welcomeMessageTimer) {
          clearInterval(this.welcomeMessageTimer);
        }
        if (this.realtimeTimer) {
          clearInterval(this.realtimeTimer);
        }
        if (this.welcomeAnimationTimer) {
          clearTimeout(this.welcomeAnimationTimer);
        }
        if (this.videoTimer) {
          clearInterval(this.videoTimer);
        }

        // 初始化进度检查定时器
        this.progressTimer = setInterval(() => {
          if (this.checkUpcomingAppointments) {
            this.checkUpcomingAppointments();
          }
        }, 1000);

        // 初始化数据刷新定时器
        this.dataRefreshTimer = setInterval(() => {
          if (this.fetchAppointmentData) {
            this.fetchAppointmentData();
          }
          if (this.fetchTodayAppointmentData) {
            this.fetchTodayAppointmentData();
          }
        }, 30 * 1000); // 30秒刷新一次

        // 初始化欢迎语轮询定时器
        this.welcomeMessageTimer = setInterval(() => {
          if (this.fetchWelcomeMessage) {
            this.fetchWelcomeMessage();
          }
        }, 20 * 1000); // 20秒检查一次

        // 初始化视频轮询定时器
        this.videoTimer = setInterval(() => {
          if (this.fetchVideoList) {
            this.fetchVideoList();
          }
        }, 60 * 1000); // 60秒检查一次

        // 初始化实时数据查询定时器
        this.realtimeTimer = setInterval(() => {
          if (this.fetchRealtimeData && typeof this.fetchRealtimeData === 'function') {
            this.fetchRealtimeData();
          }
        }, 10 * 1000); // 10秒查询一次

        // 初始化图表更新定时器
        this.chartUpdateTimer = setInterval(() => {
          if (this.updateAllCharts) {
            this.updateAllCharts();
          }
        }, chartUpdateMs);

        // 初始化脑波值每秒切换定时器
        this.$nextTick(() => {
          if (this.brainwaveDisplayTimer) {
            clearInterval(this.brainwaveDisplayTimer);
          }
          this.brainwaveDisplayTimer = setInterval(() => {
            if (this && this.updateBrainwaveDisplay && typeof this.updateBrainwaveDisplay === 'function') {
              try {
                this.updateBrainwaveDisplay();
              } catch (error) {
                logger.error('updateBrainwaveDisplay error:', error);
              }
            }
          }, brainwaveDisplayMs);
        });

        // 初始化气泡更新定时器（配置为0时关闭）
        if (bubbleUpdateMs > 0) {
          this.bubbleTimer = setInterval(() => {
            if (this.updateBubbles) {
              this.updateBubbles();
            }
          }, bubbleUpdateMs);
        } else {
          this.bubbleTimer = null;
        }

        // 启动时间更新定时器
        this.startTimeUpdateTimer();
      },

      /**
       * 清理所有定时器
       */
      clearAllTimers() {
        if (this.progressTimer) {
          clearInterval(this.progressTimer);
          this.progressTimer = null;
        }
        if (this.dataRefreshTimer) {
          clearInterval(this.dataRefreshTimer);
          this.dataRefreshTimer = null;
        }
        if (this.rotationTimer) {
          clearTimeout(this.rotationTimer);
          clearInterval(this.rotationTimer);
          this.rotationTimer = null;
        }
        if (this.chartUpdateTimer) {
          clearInterval(this.chartUpdateTimer);
          this.chartUpdateTimer = null;
        }
        if (this.bubbleTimer) {
          clearInterval(this.bubbleTimer);
          this.bubbleTimer = null;
        }
        if (this.brainwaveDisplayTimer) {
          clearInterval(this.brainwaveDisplayTimer);
          this.brainwaveDisplayTimer = null;
        }
        if (this.timeUpdateTimer) {
          clearInterval(this.timeUpdateTimer);
          this.timeUpdateTimer = null;
        }
        if (this.welcomeMessageTimer) {
          clearInterval(this.welcomeMessageTimer);
          this.welcomeMessageTimer = null;
        }
        if (this.videoTimer) {
          clearInterval(this.videoTimer);
          this.videoTimer = null;
        }
        if (this.videoPreloadTimer) {
          clearTimeout(this.videoPreloadTimer);
          this.videoPreloadTimer = null;
        }
        if (this.videoPreloadRetryTimer) {
          clearTimeout(this.videoPreloadRetryTimer);
          this.videoPreloadRetryTimer = null;
        }
        if (this.cleanupPreloadVideoTimer) {
          clearTimeout(this.cleanupPreloadVideoTimer);
          this.cleanupPreloadVideoTimer = null;
        }
        if (this.realtimeTimer) {
          clearInterval(this.realtimeTimer);
          this.realtimeTimer = null;
        }
        if (this.sleepStateTimer) {
          clearInterval(this.sleepStateTimer);
          this.sleepStateTimer = null;
        }
        if (this.hourlyRecoveryTimer) {
          clearInterval(this.hourlyRecoveryTimer);
          this.hourlyRecoveryTimer = null;
        }
        if (this.hourlyRecoveryStarter) {
          clearTimeout(this.hourlyRecoveryStarter);
          this.hourlyRecoveryStarter = null;
        }
        if (this.midnightResetTimer) {
          clearTimeout(this.midnightResetTimer);
          this.midnightResetTimer = null;
        }
        if (this.fetchAppointmentDataRetryTimer) {
          clearTimeout(this.fetchAppointmentDataRetryTimer);
          this.fetchAppointmentDataRetryTimer = null;
        }
        if (this.fetchAppointmentDataRetryTimer2) {
          clearTimeout(this.fetchAppointmentDataRetryTimer2);
          this.fetchAppointmentDataRetryTimer2 = null;
        }
        if (this.fetchAppointmentDataDelayTimer) {
          clearTimeout(this.fetchAppointmentDataDelayTimer);
          this.fetchAppointmentDataDelayTimer = null;
        }
      }
    },

    beforeDestroy() {
      this.clearAllTimers();
    }
  };
}

